import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import "@/i18n";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Countries from "@/pages/Countries";
import CountryDetail from "@/pages/CountryDetail";
import Catalog from "@/pages/Catalog";
import CandidateDetail from "@/pages/CandidateDetail";
import Booking from "@/pages/Booking";
import Pipeline from "@/pages/Pipeline";
import Process from "@/pages/Process";
import Contact from "@/pages/Contact";
import Join from "@/pages/Join";
import CustomPage from "@/pages/CustomPage";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import Portal from "@/pages/Portal";
import EmployerLogin from "@/pages/employer/EmployerLogin";
import EmployerRegister from "@/pages/employer/EmployerRegister";
import EmployerPending from "@/pages/employer/EmployerPending";
import EmployerBookings from "@/pages/employer/EmployerBookings";
import { useAuth } from "@/context/AuthContext";
import { EmployerProvider, useEmployer } from "@/context/EmployerContext";
import { BasketProvider } from "@/context/BasketContext";
import InterviewAlarm from "@/components/InterviewAlarm";
import BasketSidebar from "@/components/BasketSidebar";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-center">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function EmployerOnlyRoute({ children }) {
  const { employer, loading } = useEmployer();
  const { user, loading: adminLoading } = useAuth();
  const path = window.location.pathname + window.location.search;
  if (loading || adminLoading) return <div className="p-10 text-center">Loading…</div>;
  // Any active admin session (super_admin, admin, editor, viewer) can browse
  // the employer-only pages without registering a separate employer account.
  if (user) return children;
  // Unauthenticated visitors are sent to the /portal choice screen ("Who are
  // you?") — from there they can pick Employer sign-in or Admin sign-in.
  // The intended path is preserved in the `next` param so Portal.jsx can
  // forward it to whichever login flow the visitor selects.
  if (!employer) return <Navigate to={`/portal?next=${encodeURIComponent(path)}`} replace />;
  return children;
}

export default function App() {
  return (
    <EmployerProvider>
    <BasketProvider>
    <InterviewAlarm />
    <BasketSidebar />
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/services" element={<Services />} />
        <Route path="/countries" element={<Countries />} />
        <Route path="/countries/:id" element={<CountryDetail />} />
        <Route path="/catalog" element={<EmployerOnlyRoute><Catalog /></EmployerOnlyRoute>} />
        <Route path="/candidates/:id" element={<EmployerOnlyRoute><CandidateDetail /></EmployerOnlyRoute>} />
        <Route path="/candidates/:id/book" element={<EmployerOnlyRoute><Booking /></EmployerOnlyRoute>} />
        <Route path="/pipeline" element={<Pipeline />} />
        <Route path="/process" element={<Process />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/join" element={<Join />} />
        <Route path="/register" element={<Navigate to="/join" replace />} />
        <Route path="/p/:slug" element={<CustomPage />} />
      </Route>
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/portal" element={<Portal />} />
      <Route path="/employer/login" element={<EmployerLogin />} />
      <Route path="/employer/register" element={<EmployerRegister />} />
      <Route path="/employer/pending" element={<EmployerPending />} />
      <Route path="/employer/bookings" element={<EmployerOnlyRoute><EmployerBookings /></EmployerOnlyRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/:countrySlug" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </BasketProvider>
    </EmployerProvider>
  );
}
